import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { LocationType } from '../../types/common'
import messages from './messages'

export const getLocationValidationSchema = (formatMessage: any) => {
  return z
    .object({
      name: z.string().max(100, formatMessage(messages.nameValidation)),
      tenantId: z.number().nullable().optional(),
      customerId: z.number().nullable().optional(),
      locationTypeId: z.number().optional(),
      locationParentId: z.number().nullable().optional(),
      defaultTransportModeId: z.number().nullable().optional(),
      customerSelectable: z.boolean().optional(),
      systemDefined: z.boolean().optional(),
      enabled: z.boolean().optional(),
      validFrom: z.union([z.date(), z.string()]).nullable().optional(),
      validTo: z.union([z.date(), z.string()]).nullable().optional(),
      address: z
        .object({
          countryId: z.number().min(1, formatMessage(messages.countryValidation)),
          address1: z.string().max(120, formatMessage(messages.address1Validation)),
          address2: z.string().max(120).optional(),
          address3: z.string().max(120).optional(),
          city: z.string().max(100, formatMessage(messages.cityValidation)),
          state: z.string().max(100, formatMessage(messages.stateValidation)),
          postalCode: z.string().max(16, formatMessage(messages.postCodeValidation)),
        })
        .passthrough()
        .nullable(),
      lat: z
        .union([z.string(), z.number()])
        .nullable()
        .transform((val) => {
          if (val === null || val === '') return null
          if (typeof val === 'number') return val
          const num = parseFloat(val)
          return isNaN(num) ? null : num
        })
        .pipe(
          z
            .number()
            .nullable()
            .refine((val) => {
              if (val === null) return true
              return !isNaN(val)
            }, formatMessage(messages.latFormatValidation))
            .refine((val) => {
              if (val === null) return true
              return val >= -90 && val <= 90
            }, formatMessage(messages.latRangeValidation))
        ),
      lng: z
        .union([z.string(), z.number()])
        .nullable()
        .transform((val) => {
          if (val === null || val === '') return null
          if (typeof val === 'number') return val
          const num = parseFloat(val)
          return isNaN(num) ? null : num
        })
        .pipe(
          z
            .number()
            .nullable()
            .refine((val) => {
              if (val === null) return true
              return !isNaN(val)
            }, formatMessage(messages.lngFormatValidation))
            .refine((val) => {
              if (val === null) return true
              return val >= -180 && val <= 180
            }, formatMessage(messages.lngRangeValidation))
        ),
      geofence: z.any().optional(),
      contacts: z
        .array(
          z.object({
            email: z.string().email('Invalid email format').min(1, 'Email is required'),
            phone: z.string().max(16, 'Max allowed is 16 characters').min(1, 'Phone is required'),
            name: z.string().min(1, 'Name is required'),
            contactTypeId: z.number().min(1, 'Contact type is required'),
          })
        )
        .optional(),
    })
    .passthrough()
    .refine(
      (data) => {
        // Conditional validation for customerId when locationTypeId is Shipper
        if (data.locationTypeId === LocationType.Shipper) {
          return data.customerId && data.customerId > 0
        }
        return true
      },
      {
        message: formatMessage(messages.customerValidation),
        path: ['customerId'],
      }
    )
    .refine(
      (data) => {
        // Conditional validation for lat/lng when locationTypeId is not Shipper
        if (data.locationTypeId !== LocationType.Shipper) {
          const lat = data.lat
          const lng = data.lng
          return lat !== null && lng !== null
        }
        return true
      },
      {
        message: formatMessage(messages.latValidation),
        path: ['lat'],
      }
    )
}

export const getLocationResolver = (formatMessage: any) => {
  return zodResolver(getLocationValidationSchema(formatMessage))
}
